<template>
  <div class="kiosk-confirmation w-full sm:w-2/3 xl:w-2/5">
    <!-- Return banner -->
    <div class="kiosk-return-banner vx-card p-4 mb-4 flex items-center justify-between">
      <span class="text-sm text-content">
        Returning to start page in {{ countdown }} sec...
      </span>
      <button
        type="button"
        class="kiosk-return-now-btn text-sm font-bold text-dark"
        @click="returnNow"
      >
        Return now
      </button>
    </div>

    <!-- Confirmation card -->
    <div class="vx-card kiosk-confirm-card p-6 sm:p-10 text-center">
      <!-- Success icon -->
      <div class="kiosk-success-icon-container my-8">
        <vs-icon
          icon-pack="material-icons"
          icon="check"
          size="70px"
          :color="accentColour"
          class="kiosk-success-icon"
        />
      </div>

      <h2 class="text-xl md:text-2xl font-semibold text-dark mb-3 leading-snug">
        You're all set
      </h2>
      <p class="text-base text-content mb-8 leading-relaxed">
        Your spot has been successfully reserved. We'll notify you when it's time to head back.
      </p>

      <!-- Queue stats -->
      <div class="vx-card bg-theme-background p-6 mb-8">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
          <div class="text-center">
            <div class="text-sm text-content mb-1 font-medium uppercase tracking-wider">Your spot</div>
            <div class="text-2xl font-bold text-dark">{{ queuePositionDisplay }}</div>
          </div>
          <div class="text-center">
            <div class="text-sm text-content mb-1 font-medium uppercase tracking-wider">Spots ahead</div>
            <div class="text-2xl font-bold text-dark">{{ spotsAhead }}</div>
          </div>
        </div>

        <!-- Customer details -->
        <div class="vx-card p-4">
          <h4 class="text-sm text-content mb-3 font-semibold uppercase tracking-wider">Your details</h4>
          <div class="text-left">
            <div v-for="(detail, index) in visibleDetails" :key="detail.label">
              <div class="flex flex-col sm:flex-row sm:justify-between py-2">
                <span class="text-sm text-content font-medium mr-4 mb-1 sm:mb-0">{{ detail.label }}</span>
                <span class="text-sm text-dark font-semibold sm:text-right break-all min-w-0">{{ detail.value }}</span>
              </div>
              <vs-divider v-if="index < visibleDetails.length - 1" class="my-2" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KioskConfirmation',
  props: {
    accentColour: {
      type: String,
      required: true,
    },
    turnInfo: {
      type: Object,
      required: true,
    },
    customerName: {
      type: String,
      default: '',
    },
    customerPhone: {
      type: String,
      default: '',
    },
    customerEmail: {
      type: String,
      default: '',
    },
    prefixQueuePosition: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      countdown: 5,
      countdownInterval: null,
    };
  },
  computed: {
    queuePositionDisplay() {
      const turnNumber = this.turnInfo && this.turnInfo.waitingList
        ? this.turnInfo.waitingList.turnNumber
        : '';
      const separator = this.prefixQueuePosition ? '-' : '';
      return `${this.prefixQueuePosition || ''}${separator}${turnNumber}`;
    },
    spotsAhead() {
      return this.turnInfo ? (this.turnInfo.spotsAhead || 0) : 0;
    },
    visibleDetails() {
      const details = [];
      if (this.customerName) {
        details.push({ label: 'Name', value: this.customerName });
      }
      if (this.customerPhone) {
        details.push({ label: 'Phone', value: this.customerPhone });
      }
      if (this.customerEmail) {
        details.push({ label: 'Email', value: this.customerEmail });
      }
      return details;
    },
  },
  mounted() {
    this.startCountdown();
  },
  beforeDestroy() {
    this.stopCountdown();
  },
  methods: {
    startCountdown() {
      this.countdown = 5;
      this.countdownInterval = setInterval(() => {
        this.countdown -= 1;
        if (this.countdown <= 0) {
          this.stopCountdown();
          this.$emit('return');
        }
      }, 1000);
    },
    stopCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    },
    returnNow() {
      this.stopCountdown();
      this.$emit('return');
    },
  },
};
</script>

<style lang="scss" scoped>
.kiosk-confirmation {
  animation: slideUp 0.6s ease;
}

.kiosk-return-banner {
  border-radius: 12px;
  border: 1px solid #e4e7eb;
}

.kiosk-return-now-btn {
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  padding: 4px 8px;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
}

.kiosk-confirm-card {
  border-radius: 16px;
  position: relative;
  overflow: hidden;

  // Mobile: hide card styling
  @media (max-width: 575.98px) {
    &.vx-card {
      background: transparent;
      border: none;
      box-shadow: none;
    }
  }
}

.kiosk-success-icon-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.kiosk-success-icon {
  animation: scaleIn 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  z-index: 2;
}

.kiosk-success-icon-container::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 112px;
  height: 112px;
  background: v-bind('accentColour');
  opacity: 0.1;
  border-radius: 50%;
  z-index: 1;
}

@keyframes scaleIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes slideUp {
  0% {
    transform: translateY(20px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
